import * as React from 'react';
import { BaseProps, IAddonsProps } from '../common/props';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { InputAddons } from '../input-addons/InputAddons';
import { Icon } from '../icon/Icon';
import { mcShowFalse, mcShowTrue } from '@mc/react-ui-icons';
import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';

export type PasswordInputProps = IAddonsProps &
  BaseProps & {
    disabled?: boolean;
    readOnly?: boolean;

    value: string;
    onChange?: (newValue?: string) => void;
    onBlur?: (newValue?: string) => void;
    onFocus?: () => void;
    placeholder?: string;
    inputProps?: BaseProps & React.HTMLProps<HTMLInputElement>;
    autoFocus?: boolean;
  };

/**
 * Komponenta pro editaci hesla.
 */
export const PasswordInput: React.FC<PasswordInputProps> = ({
  className,
  addonAfter,
  addonAfterCompact,
  onBlur,
  onFocus,
  inputProps,
  value,
  onChange,
  disabled,
  placeholder,
  title,
  style,
  readOnly,
  dataTest,
  autoFocus,
  ...other
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [inputValue, setInputValue] = useState<string>(value);
  const inputRef = useRef<HTMLInputElement>();

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);
  const showHidePassword = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setShowPassword(!showPassword);
  };
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        let newValue;
        if (inputValue === '') {
          newValue = undefined;
        } else {
          newValue = inputValue;
        }
        onChange && onChange(newValue);
        onBlur && onBlur(newValue);
        break;
    }
  };

  const input = (
    <div
      key={'input'}
      className={classNames(
        className,
        Classes.PASSWORD_INPUT,
        Classes.FIELD_INPUT,
        Classes.FIELD_HAS_INPUT_ACTION,
        {
          [Classes.FIELD_INPUT_DISABLED]: disabled,
          [Classes.FIELD_INPUT_READONLY]: readOnly,
        }
      )}
      {...other}
    >
      <div key="input-wrapper" className={Classes.FIELD_INPUT_GROUP}>
        <input
          data-test={dataTest ? `input-${dataTest}` : undefined}
          key={'input'}
          {...inputProps}
          disabled={disabled}
          placeholder={placeholder}
          readOnly={readOnly}
          onKeyDown={handleKeyDown}
          // @ts-ignore
          ref={inputRef}
          className={classNames(
            Classes.INPUT,
            Classes.PASSWORD_INPUT_INPUT,
            inputProps?.className,
            { [Classes.INPUT_ADDONS_COMPACT]: addonAfterCompact }
          )}
          style={style}
          type={showPassword ? 'text' : 'password'}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={onFocus}
          onBlur={() => {
            let newValue;
            if (inputValue === '') {
              newValue = undefined;
            } else {
              newValue = inputValue;
            }
            onChange && onChange(newValue);
            onBlur && onBlur(newValue);
          }}
          autoFocus={autoFocus}
        />
        <div
          key={'icon'}
          tabIndex={-1}
          className={Classes.FIELD_INPUT_ACTION}
          onClick={(e) =>
            disabled || readOnly ? undefined : showHidePassword(e)
          }
        >
          <Icon size={'lg'} icon={showPassword ? mcShowFalse : mcShowTrue} />
        </div>
      </div>
    </div>
  );

  return (
    <InputAddons addonAfter={addonAfter} addonAfterCompact={addonAfterCompact}>
      {input}
    </InputAddons>
  );
};
